import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { RouterLink, ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { OperatorService } from '../../../services/operator.service';
import { DataSharingService } from '../../../services/data-sharing.service';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { finalize, Subscription, filter } from 'rxjs';
import { NotificationService } from '../../../services/notification.service';

@Component({
    selector: 'app-operation-installation-removal-detail',
    imports: [
        CommonModule,
        FormsModule,
        CalendarModule,
        RouterLink,
        ButtonModule,
        InputTextModule,
        TableModule,
        NgClass
    ],
    templateUrl: './operation-installation-removal-detail.component.html',
    styleUrl: './operation-installation-removal-detail.component.scss'
})
export class OperationInstallationRemovalDetailComponent implements OnInit, OnDestroy {
  // Search parameters
  tagNumber: string = '';
  isLoading: boolean = false;

  // Flags to control visibility of sections
  showOperations: boolean = false;
  showRepairTypeTagNumber: boolean = true;

  // Subscription for router events
  private routerSubscription: Subscription | null = null;

  // Operation data fields
  operationData: any = {
    dateEntered: '',
    operatorCAI: '',
    tlrStatus: '',
    repairType: '',
    tagNumber: '',
    division: '',
    section: '',
    plant: '',
    shutdownDescription: '',
    associatedPlant: '',
    rule1173: '',
    leakingFrom: '',
    otherLeakType: '',
    operatingPSI: '',
    operatingTemp: '',
    vaporLiquid: '',
    service: '',
    otherService: '',
    permanentRepair: '',
    technicalJustification: '',
    tlrInstallationDate: null,
    tlrRemovalDate: null
  };

  // Date objects for calendar components
  installationDate: string = '';
  removalDate: string | null = '';

  constructor(
    private operatorService: OperatorService,
    private messageService: NotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private dataSharingService: DataSharingService
  ) { }

  ngOnInit() {
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
    });

    // Check if query parameters are available
    this.route.queryParams.subscribe(params => {
      if (params['tagNumber']) {
        this.tagNumber = params['tagNumber'];

        // If dataAvailable is true, get data from DataSharingService
        if (params['dataAvailable'] === 'true') {
          const data = this.dataSharingService.getOperationDataSnapshot();
          if (data) {
            this.mapOperationData(data);
            this.showOperations = true;
            this.showRepairTypeTagNumber = false;
          } else {
            this.getOperationData();
          }
        } else {
          const data = this.dataSharingService.getOperationDataSnapshot();

          // If we have stored data for this tag number, use it
          if (data && data.tagNumber === this.tagNumber) {
            this.mapOperationData(data);
            this.showOperations = true;
            this.showRepairTypeTagNumber = false;
          } else {
            this.getOperationData();
          }
        }
      } else {
        // No tag number in URL, check if we have a last used tag number
        const lastTagNumber = this.dataSharingService.getLastTagNumber();
        if (lastTagNumber) {
          this.tagNumber = lastTagNumber;

          // Try to get data for this tag number
          const data = this.dataSharingService.getOperationDataSnapshot();
          if (data && data.tagNumber === this.tagNumber) {
            this.mapOperationData(data);
            this.showOperations = true;
            this.showRepairTypeTagNumber = false;
          } else {
            // Reset if no data found for last tag number
            this.resetComponentState();
          }
        } else {
          this.resetComponentState();
        }
      }
    });
  }

  ngOnDestroy() {
    // Clean up subscriptions
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    // Don't clear data from DataSharingService on component destroy
    // Data should remain for page refreshs
    // this.dataSharingService.clearOperationData();
  }

  // Reset the component state
  resetComponentState() {
    this.tagNumber = '';
    this.showOperations = false;
    this.showRepairTypeTagNumber = true;
    this.installationDate = '';
    this.removalDate = null;
    this.operationData = {
      dateEntered: '',
      operatorCAI: '',
      tlrStatus: '',
      repairType: '',
      tagNumber: '',
      division: '',
      section: '',
      plant: '',
      shutdownDescription: '',
      associatedPlant: '',
      rule1173: '',
      leakingFrom: '',
      otherLeakType: '',
      operatingPSI: '',
      operatingTemp: '',
      vaporLiquid: '',
      service: '',
      otherService: '',
      permanentRepair: '',
      technicalJustification: '',
      tlrInstallationDate: null,
      tlrRemovalDate: null
    };
  }

  // Map API data to our display format
  mapOperationData(data: any) {
    if (!data) return;
    this.operationData = {
      dateEntered: data.deDateEntered ? new Date(data.deDateEntered).toLocaleString() : '',
      operatorCAI: data.decai || '',
      tlrStatus: this.mapTlrStatus(data.tlrStatus),
      repairType: data.repairTypeName || '',
      tagNumber: data.tagNumber || '',
      division: data.divisionName || '',
      section: data.sectionName || '',
      plant: data.plantName || '',
      shutdownDescription: data.shutdownDescription || '',
      associatedPlant: data.assocPlantShutdown || '',
      rule1173: data.rule1173 || '',
      leakingFrom: data.leakingName || '',
      otherLeakType: data.leakingOther || '',
      operatingPSI: data.operatingPSI?.toString() || '',
      operatingTemp: data.operatingTemperature?.toString() || '',
      vaporLiquid: data.materialStateName || '',
      service: data.serviceOther || '',
      otherService: '',
      permanentRepair: data.permRepairComplete === "1" ? "Yes" : "No",
      technicalJustification: data.technicalJustification || '',
      // Keep tlrInstallationDate as a Date object instead of converting to string
      tlrInstallationDate: data.tlrInstallationDate ? new Date(data.tlrInstallationDate) : null,
      tlrRemovalDate: data.tlrRemovalDate ? new Date(data.tlrRemovalDate) : null
    };

    // Set dates for calendar components
    if (data.tlrInstallationDate) {
      this.installationDate = data.tlrInstallationDate;
    }

    if (data.tlrRemovalDate) {
      this.removalDate = data.tlrRemovalDate;
    }
  }

  // Fetch operation data by tag number
  getOperationData() {
    if (!this.tagNumber) {
      this.messageService.showError('Please enter a tag number');
      return;
    }

    this.isLoading = true;
    this.showOperations = false;
    this.operatorService.getOperationByTagNumber(this.tagNumber)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (data) => {
          if (data) {
            this.dataSharingService.setOperationData(data);

            // Map API response to operationData format
            this.mapOperationData(data);

            // Show operations section and hide repair type tag number section
            this.showOperations = true;
            this.showRepairTypeTagNumber = false;
          } else {
            this.messageService.showError(`No operation found with tag number ${this.tagNumber}`
            );

            // Keep operations section hidden and repair type tag number section visible
            this.showOperations = false;
            this.showRepairTypeTagNumber = true;
          }
        },
        error: (error) => {
          this.messageService.showError('Failed to fetch operation data'
          );

          // Keep operations section hidden and repair type tag number section visible
          this.showOperations = false;
          this.showRepairTypeTagNumber = true;
        }
      });
  }

  // Helper function to map TLR status codes to readable text
  mapTlrStatus(code: string): string {
    if (!code) return '';
    const statusMap: { [key: string]: string } = {
      'I': 'Installed',
      'R': 'Removed',
      'P': 'Pre-Installation'
    };
    return statusMap[code] || code;
  }

  onSubmit() {
    this.isLoading = true;
    // Format dates to YYYY-MM-DD if they exist
    const formatDate = (date: any) => {
      if (!date) return null;
      
      if (typeof date === 'string' && date.length >= 10) {
        // If it's already a string in date format
        const parsedDate = new Date(date);
        return `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`;
      }
      
      if (date instanceof Date) {
        // Format the date in YYYY-MM-DD format, preserving the selected date without timezone offset
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      }
      
      return null;
    };
    
    // Create simplified payload for the API - service will add facility ID and CAI
    const payload = {
      "recID": 0,
      "tagNumber": this.tagNumber,
      "tlrInstallationDate": formatDate(this.operationData.tlrInstallationDate),
      "tlrRemovalDate": formatDate(this.operationData.tlrRemovalDate)
    };

    // Show loading state
    this.isLoading = true;

    // Call the API to save dates
    this.operatorService.AddOrUpdateTLRInstallationInfo(payload)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.messageService.showSuccess('Installation/Removal dates saved successfully');

          // Update the data in the DataSharingService with the new dates
          const currentData = this.dataSharingService.getOperationDataSnapshot();
          
          // Update TLR status based on dates
          if(this.operationData.tlrInstallationDate) {
            this.operationData.tlr_status = 'I';
            this.operationData.tlrStatus = 'Installed';
          }
          if(this.operationData.tlrRemovalDate) {
            this.operationData.tlr_status = 'R';
            this.operationData.tlrStatus = 'Removed';
          }
          
          // Update the shared data with correct date objects
          if (currentData) {
            // Ensure dates are properly set in the shared data
            if (payload.tlrInstallationDate) {
              currentData.tlrInstallationDate = new Date(payload.tlrInstallationDate + 'T00:00:00');
            } else {
              currentData.tlrInstallationDate = null;
            }
            
            if (payload.tlrRemovalDate) {
              currentData.tlrRemovalDate = new Date(payload.tlrRemovalDate + 'T00:00:00');
            } else {
              currentData.tlrRemovalDate = null;
            }
            
            currentData.tlr_status = this.operationData.tlr_status;
            this.dataSharingService.setOperationData(currentData);
          }
        },
        error: (error) => {
          this.messageService.showError('Failed to save installation/removal dates');
        }
      });
  }
}
